import { useIntl } from 'react-intl'

import {
  type OfferCheapestPriceV1,
  type OfferPriceV1,
  type OfferV11,
  type PimProductV11,
  type PimProductV1StockStatusEnum,
  type PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import { useGlobalConfigContext } from '../../../../global-config'
import { TENANTS_WITH_MARKETPLACE_FUNCTIONALITY } from '../../../../marketplace'
import {
  type ProductDiscountBoundaries,
  productIsValidDiscount,
  useProductConfigContext,
} from '../../../../product'
import { type TENANT } from '../../../../tenant'
import { offerAccessorIsOfferMirakl } from '../../../model/offerAccessor/offerAccessorIsOfferMirakl'
import { priceAccessorGetDisplayPrice } from '../../../model/priceAccessor/priceAccessorGetDisplayPrice'
import {
  type ErxRecommendedRetailPrice,
  priceAccessorGetErxRecommendedRetailPrice,
} from '../../../model/priceAccessor/priceAccessorGetErxRecommendedRetailPrice'
import { priceAccessorGetFormattedUnitPrice } from '../../../model/priceAccessor/priceAccessorGetFormattedUnitPrice'
import { priceAccessorGetThirtyDaysLowestPrice } from '../../../model/priceAccessor/priceAccessorGetThirtyDaysLowestPrice'
import {
  priceAccessorGetThirtyDaysPriceTrend,
  PRODUCT_THIRTY_DAYS_PRICE_TREND,
} from '../../../model/priceAccessor/priceAccessorGetThirtyDaysPriceTrend'
import { productAccessorGetDiscountFromOffer } from '../../../model/productAccessor/productAccessorGetDiscountFromOffer'
import { productAccessorGetOfferByIdOrDefault } from '../../../model/productAccessor/productAccessorGetOfferByIdOrDefault'
import { productAccessorIsProductNotInCatalog } from '../../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../../model/productAccessor/productAccessorIsProductOutOfStock'
import {
  productAccessorGetReferencePriceType,
  productAccessorShouldUseReferencePrice,
  type ProductReferencePriceType,
} from '../../../model/productAccessor/productAccessorReferencePrice'
import { useShowProductThirtyDaysPrice } from '../useShowProductThirtyDaysPrice'

export type ProductPrices = {
  discount: number
  displayPrice: OfferPriceV1 | null
  erxRecommendedRetailPrice: ErxRecommendedRetailPrice | null
  formattedUnitPrice: string | null
  isDiscountValid: boolean
  isOfferMirakl: boolean
  isProductNotInCatalog: boolean
  isProductOutOfSale: boolean
  isProductOutOfStock: boolean
  isThirtyDayPriceTrendDown: boolean
  referencePriceType: ProductReferencePriceType | null
  shouldUseReferencePrice: boolean
  showThirtyDaysPrice: boolean
  strikeThroughOfferPrice: OfferPriceV1 | null
  thirtyDayPriceTrend: PRODUCT_THIRTY_DAYS_PRICE_TREND
  thirtyDaysLowestPrice: OfferCheapestPriceV1 | null
}

/**
 * default empty prices
 */
const getDefaultEmptyPrices = (
  stock_status: PimProductV1StockStatusEnum | undefined,
  offer?: OfferV11,
): ProductPrices => ({
  discount: 0,
  displayPrice: null,
  erxRecommendedRetailPrice: null,
  formattedUnitPrice: null,
  isDiscountValid: false,
  /**
   * it could be that stock status not available but offer is mirakl, we still need to show seller box with correct seller
   */
  isOfferMirakl: offerAccessorIsOfferMirakl(offer?.offerType),
  isProductNotInCatalog: productAccessorIsProductNotInCatalog(stock_status),
  isProductOutOfSale: productAccessorIsProductOutOfSale(stock_status),
  isProductOutOfStock: productAccessorIsProductOutOfStock(stock_status),
  isThirtyDayPriceTrendDown: false,
  referencePriceType: null,
  shouldUseReferencePrice: false,
  showThirtyDaysPrice: false,
  strikeThroughOfferPrice: null,
  thirtyDayPriceTrend: PRODUCT_THIRTY_DAYS_PRICE_TREND.PRICE_NO_CHANGE,
  thirtyDaysLowestPrice: null,
})

const getProductReferencePrice = ({
  discount,
  isDiscountValid,
  isOfferMirakl,
  product,
  referencePriceAmount,
  tenant,
}: {
  discount: number
  isDiscountValid: boolean
  isOfferMirakl: boolean
  product: PimProductV11
  referencePriceAmount?: number | null
  tenant: TENANT
}): Pick<ProductPrices, 'referencePriceType' | 'shouldUseReferencePrice'> => {
  const referencePriceType = productAccessorGetReferencePriceType({
    product,
  })
  const shouldUseReferencePrice = productAccessorShouldUseReferencePrice({
    discount,
    isDiscountValid,
    isOfferMirakl,
    product,
    referencePriceAmount,
    tenant,
  })

  return {
    referencePriceType: shouldUseReferencePrice ? referencePriceType : null,
    shouldUseReferencePrice,
  }
}

const getProductPriceFlags = ({
  discount,
  discountBoundaries,
  offer,
  product,
  tenant,
  thirtyDayPriceTrend,
}: {
  discount: number
  discountBoundaries?: ProductDiscountBoundaries
  offer: OfferV11
  product: PimProductV11
  tenant: TENANT
  thirtyDayPriceTrend: PRODUCT_THIRTY_DAYS_PRICE_TREND
}): Pick<
  ProductPrices,
  | 'isDiscountValid'
  | 'isOfferMirakl'
  | 'isThirtyDayPriceTrendDown'
  | 'referencePriceType'
  | 'shouldUseReferencePrice'
> => {
  const isDiscountValid = productIsValidDiscount(discount, discountBoundaries)
  const isOfferMirakl = offerAccessorIsOfferMirakl(offer.offerType)

  return {
    isDiscountValid,
    isOfferMirakl,
    isThirtyDayPriceTrendDown:
      thirtyDayPriceTrend === PRODUCT_THIRTY_DAYS_PRICE_TREND.PRICE_DOWN,
    ...getProductReferencePrice({
      discount,
      isDiscountValid,
      isOfferMirakl,
      product,
      referencePriceAmount: offer.strikeThroughOfferPrice?.price,
      tenant,
    }),
  }
}

export const useProductPrices = ({
  currentPrescriptionType,
  offerId,
  product,
}: {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  offerId?: string | null
  product: PimProductV11
}): ProductPrices => {
  const { tenant } = useGlobalConfigContext()
  const { discountBoundaries } = useProductConfigContext()
  const isMarketplace = TENANTS_WITH_MARKETPLACE_FUNCTIONALITY.has(tenant)
  const intl = useIntl()

  const offer = productAccessorGetOfferByIdOrDefault({
    isMarketplace,
    offerId,
    offers: product.offers,
  })

  const showThirtyDaysPrice = useShowProductThirtyDaysPrice(offer)

  const isProductOutOfStock = productAccessorIsProductOutOfStock(
    product.stock_status,
  )

  if (isProductOutOfStock || !offer) {
    return getDefaultEmptyPrices(product.stock_status, offer)
  }

  const discount = productAccessorGetDiscountFromOffer({
    currentPrescriptionType,
    offer,
    tenant,
  })

  /*
   * StrikeThroughOfferPrice currency should be the same as OfferPrice currency but for some reason on CH it's not
   * https://jira.shop-apotheke.com/browse/WSAWA-4040
   */
  if (
    offer.strikeThroughOfferPrice &&
    offer.offerPrice &&
    offer.strikeThroughOfferPrice.currency !== offer.offerPrice.currency
  ) {
    // eslint-disable-next-line react-hooks/immutability -- don't reference, refactor if touched
    offer.strikeThroughOfferPrice.currency = offer.offerPrice.currency
  }

  const thirtyDayPriceTrend = priceAccessorGetThirtyDaysPriceTrend(
    offer,
    currentPrescriptionType,
  )
  const productPriceFlags = getProductPriceFlags({
    discount,
    discountBoundaries,
    offer,
    product,
    tenant,
    thirtyDayPriceTrend,
  })

  return {
    discount,
    displayPrice: priceAccessorGetDisplayPrice(offer, currentPrescriptionType),
    erxRecommendedRetailPrice: priceAccessorGetErxRecommendedRetailPrice(
      offer,
      currentPrescriptionType,
    ),
    formattedUnitPrice: priceAccessorGetFormattedUnitPrice(offer, intl),
    isProductNotInCatalog: productAccessorIsProductNotInCatalog(
      product.stock_status,
    ),
    isProductOutOfSale: productAccessorIsProductOutOfSale(product.stock_status),
    isProductOutOfStock,
    ...productPriceFlags,
    showThirtyDaysPrice,
    strikeThroughOfferPrice: offer.strikeThroughOfferPrice ?? null,
    thirtyDayPriceTrend,
    thirtyDaysLowestPrice: priceAccessorGetThirtyDaysLowestPrice(offer),
  }
}
